import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  buildHttpParams,
  CybersocXDRDashboardSearchCriteria,
  CybersocXDRDashboardSearchCriteriaKey,
} from '../../model/searchCriteria';
import { CaseXDR, ExtendedCaseXDR } from '../../model/cybersocxdr/microsocticket';
import { SlideData, CommitteeShort, CommitteeData } from '../../model/cybersocxdr/committeeData';
import { SlaGauge } from '../../model/cybersocxdr/slagauge';
import { OrganizationNode } from '../../model/cybersocxdr/organizationNode';
import { firstValueFrom } from 'rxjs';
import { IncidentScale } from '../../model/cybersocxdr/incidentScale';
import { CountOvertime } from '../../model/cybersocxdr/countOvertime';
import { IncidentCount } from '../../model/cybersocxdr/incidentCount';
import { ClientInfos } from '../../model/cybersocxdr/microsocClientInfo';

const ROOT_URL = environment.endpoints.cybersocxdr;
const INCIDENTS_URL = ROOT_URL + 'incidents';
const HISTOGRAM_URL = INCIDENTS_URL + '/histogram';
const COMMITTEE_URL = ROOT_URL + 'committees';
const CLIENT_URL = ROOT_URL + 'clients';

type QueryOptions = {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | string[] };
  withCredentials?: boolean;
};

@Injectable()
export class CybersocxdrService {
  constructor(private http: HttpClient) {}

  /** Wrapper for the promise and error handler */
  genericRequest<T>(method: string, url: string, options?: QueryOptions): Promise<T> {
    return firstValueFrom(this.http.request<T>(method, url, options))
      .then((response) => response)
      .catch(this.handleError);
  }

  genericGet<T>(url: string, options?: QueryOptions): Promise<T> {
    return this.genericRequest('get', url, options);
  }

  genericPost<T>(url: string, options?: QueryOptions): Promise<T> {
    return this.genericRequest('post', url, options);
  }

  async getDashboardData(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/severity', {
      params: buildHttpParams(searchCriteria, new HttpParams(), ['groupLevel1', 'groupLevel2']),
    });
  }

  async getIncidentsOverTime(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    const params = new HttpParams();

    return await this.genericGet<any>(INCIDENTS_URL, {
      params: buildHttpParams(searchCriteria, params, [
        'groupLevel1',
        'groupLevel2',
        'repartitionGroupbyLevel',
        'groupbyField',
        'subGroupbyField',
      ]),
    });
  }

  async getMTTDOverTime(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/mttd', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getMTTIOverTime(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/mtti', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getOpenTimeAVGOvertime(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/opentimeavg', {
      params: buildHttpParams(searchCriteria, new HttpParams(), ['groupLevel1', 'groupLevel2']),
    });
  }

  async getHistogramIncidents(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    const params = new HttpParams();

    return await this.genericGet<any>(HISTOGRAM_URL, {
      params: buildHttpParams(searchCriteria, params, [
        'groupLevel1',
        'groupLevel2',
        'repartitionGroupbyLevel',
        'groupbyField',
        'subGroupbyField',
      ]),
    });
  }

  async getLeveledGroupedIncidents(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    const params = new HttpParams();

    return await this.genericGet<any>(INCIDENTS_URL + '/leveled', {
      params: buildHttpParams(searchCriteria, params, [
        'groupLevel1',
        'groupLevel2',
        'repartitionGroupbyLevel',
        'groupbyField',
        'subGroupbyField',
      ]),
    });
  }

  async getIncidentsRepartitions(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/repartition', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getTopIncidentsByRepartitions(
    searchCriteria: CybersocXDRDashboardSearchCriteria,
    groupByLevel: string
  ): Promise<any> {
    const params: CybersocXDRDashboardSearchCriteriaKey[] = [];
    if (groupByLevel === '2') {
      // For example, when looking at the top 5 opcos in france
      params.push('groupLevel1');
      params.push('groupLevel2');
    }
    const data = await this.genericGet<any>(INCIDENTS_URL + '/repartition', {
      params: buildHttpParams(searchCriteria, new HttpParams(), params),
    });
    return data?.filter((value) => value.id.split('.')[0] === groupByLevel);
  }

  async getTruePositiveDetails(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/true-positive-details', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getHistogramSLANotifStatus(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(HISTOGRAM_URL + '/notification', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getHistogramSLAInvestStatus(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(HISTOGRAM_URL + '/investigation', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getCategoryHeatMap(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/category-heatmap', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getVolumeBubbles(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/volume-bubbles', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getSLANotifGauge(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<SlaGauge> {
    return await this.genericGet<any>(INCIDENTS_URL + '/sla/notification', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getSLAInvestGauge(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<SlaGauge> {
    return await this.genericGet<any>(INCIDENTS_URL + '/sla/investigation', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getSLANotifStatusRate(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/sla/notification/rate', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async getSLAInvestStatusRate(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<any> {
    return await this.genericGet<any>(INCIDENTS_URL + '/sla/investigation/rate', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async isMicrosocXDRosmClient(): Promise<boolean> {
    return await this.genericGet<boolean>(CLIENT_URL + '/ismicrosocxdrclient').catch((error) => false);
  }

  async getCases(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<CaseXDR[]> {
    let params = new HttpParams();
    if (searchCriteria.sortBy) {
      params = params.append('sortBy', searchCriteria.sortBy.field);
      params = params.append('sortType', searchCriteria.sortBy.type);
    }
    params = buildHttpParams(searchCriteria, params, [
      'groupLevel1',
      'groupLevel2',
      'limit',
      'skip',
      'slaInvestigationStatusBroken',
      'slaNotificationStatusBroken',
    ]);
    return await this.genericGet<CaseXDR[]>(INCIDENTS_URL + '/cases', {
      params,
    });
  }

  async casesCSVExport(searchCriteria: CybersocXDRDashboardSearchCriteria) {
    let params = new HttpParams();
    if (searchCriteria.sortBy) {
      params = params.append('sortBy', searchCriteria.sortBy.field);
      params = params.append('sortType', searchCriteria.sortBy.type);
    }
    params = buildHttpParams(searchCriteria, params, []);
    return this.http.get(INCIDENTS_URL + '/cases/csv', {
      params,
      responseType: 'blob',
    });
  }

  /** Get a single case data */
  async getCase(id: number): Promise<ExtendedCaseXDR> {
    return await this.genericGet(INCIDENTS_URL + `/case/${id}`);
  }

  async getFlaggedCases(flags: string[]): Promise<any> {
    const getFlaggedCasesdata: ExtendedCaseXDR[] = [];

    for (const flag of flags) {
      const data = await this.getCase(parseInt(flag, 10));
      getFlaggedCasesdata.push(data);
    }
    return getFlaggedCasesdata;
  }

  async countCases(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<IncidentCount> {
    const keys: CybersocXDRDashboardSearchCriteriaKey[] = [
      'slaInvestigationStatusBroken',
      'slaNotificationStatusBroken',
    ];
    return await this.genericGet<IncidentCount>(INCIDENTS_URL + '/cases/count', {
      params: buildHttpParams(searchCriteria, new HttpParams(), keys),
    });
  }

  async getSimilarCases(id: number): Promise<Array<CaseXDR>> {
    return await this.genericGet(INCIDENTS_URL + `/case/${id}/similar`);
  }

  async getCasesBySubject(value): Promise<Array<CaseXDR>> {
    let params = new HttpParams();
    params = params.append('subject', value);
    return await this.genericGet(INCIDENTS_URL + `/cases/searchBySubject`, { params });
  }

  async getSimilarCasesCountOvertime(id: number): Promise<Array<CountOvertime>> {
    return await this.genericGet(INCIDENTS_URL + `/case/${id}/similar/count`);
  }

  async getCasesGroupBy(searchCriteria, groupBy: string): Promise<Array<CaseXDR>> {
    return await this.genericGet(INCIDENTS_URL + `/cases/groupby/${groupBy}`, {
      params: buildHttpParams(searchCriteria, new HttpParams(), ['groupLevel1', 'groupLevel2']),
    });
  }

  async getCategoryCountOvertime(id: number): Promise<Array<CountOvertime>> {
    return await this.genericGet(INCIDENTS_URL + `/case/${id}/category/count`);
  }

  async getHighlights(): Promise<any> {
    return await this.genericGet<any>(COMMITTEE_URL + '/highlights');
  }

  async getGantt(): Promise<any> {
    return await this.genericGet<any>(COMMITTEE_URL + '/gantt');
  }

  /**
   * Get the contact list of a client
   * The OCD diagram IS specific on a per client basis.
   */
  async getOrganization(): Promise<OrganizationNode> {
    let params = new HttpParams();
    params = params.append('ocdOrganization', 'true');
    return await this.genericGet<any>(CLIENT_URL + '/organization', { params });
  }

  async getCommitteeInfo(committeeId: number): Promise<SlideData> {
    return await this.genericGet<SlideData>(COMMITTEE_URL + '/' + committeeId);
  }

  async listCommittee(): Promise<Array<CommitteeShort>> {
    return await this.genericGet<Array<CommitteeShort>>(COMMITTEE_URL);
  }

  async previewCommittee(committeeData: CommitteeData): Promise<any> {
    let params = new HttpParams();
    if (committeeData.createdFrom) {
      params = params.append('createdFrom', committeeData.createdFrom);
    }
    if (committeeData.createdTo) {
      params = params.append('createdTo', committeeData.createdTo);
    }
    return await this.genericGet<any>(COMMITTEE_URL + '/preview', { params });
  }

  createCommittee(committeeData: CommitteeData): Promise<any> {
    return firstValueFrom(this.http.post(environment.endpoints.cybersocxdr + 'committees', committeeData))
      .then((response) => response)
      .catch(this.handleError);
  }

  async getClientInfo(): Promise<ClientInfos> {
    return await this.genericGet<ClientInfos>(CLIENT_URL + '/info');
  }

  async getClientContacts(): Promise<Array<{ role: string; num: string }>> {
    return await this.genericGet<Array<{ role: string; num: string }>>(CLIENT_URL + '/contacts');
  }

  async getClientTechnologies(): Promise<Array<any>> {
    return await this.genericGet<Array<object>>(CLIENT_URL + '/technologies');
  }

  async getIncidentCaseScale(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<IncidentScale> {
    return await this.genericGet<IncidentScale>(INCIDENTS_URL + '/cases/scale', {
      params: buildHttpParams(searchCriteria, new HttpParams(), []),
    });
  }

  async flagCase(flagCase: string): Promise<any> {
    return await this.genericGet<IncidentScale>(INCIDENTS_URL + '/cases/flagIncident', {
      params: new HttpParams().set('flagCase', flagCase),
    });
  }

  async getFilteredRepartitionData(
    searchCriteria: CybersocXDRDashboardSearchCriteria,
    groupByLevel: string
  ): Promise<any[]> {
    const data = await this.getIncidentsRepartitions(searchCriteria);
    return data?.filter((value) => value.id.split('.')[0] === groupByLevel);
  }

  async getGroupByFilterTypes(): Promise<string[]> {
    return await this.genericGet<string[]>(CLIENT_URL + '/repartition-filter-names');
  }

  async getAdvancedFilterValues(): Promise<any> {
    return await this.genericGet<string[][]>(INCIDENTS_URL + '/advanced-filters-values');
  }

  async getHasAnsweredSurvey(): Promise<boolean> {
    return await this.genericGet<boolean>(CLIENT_URL + '/survey/up-to-date');
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}
